<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Land Rates</span>
            </nav>
            <h1 class="page-title">Land Rates Payment</h1>
            <p class="page-subtitle">Pay your land rates quickly and securely online</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  </div>
                  <h3>Land Rates</h3>
                </div>
                <p class="info-description">
                  Land rates are annual charges levied on property owners to fund county services and infrastructure development.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>M-Pesa Payment</strong>
                      <p>Pay instantly via mobile money</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Instant Receipt</strong>
                      <p>Get your receipt immediately</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                    </div>
                    <div>
                      <strong>Secure & Safe</strong>
                      <p>Your data is protected</p>
                    </div>
                  </div>
                </div>

                <div class="info-help">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    How to find your Plot Number
                  </h4>
                  <ul>
                    <li>Check your title deed</li>
                    <li>Previous land rate receipts</li>
                    <li>Property sale agreement</li>
                    <li>Visit the Lands office</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Right Side - Form -->
            <div class="form-panel">
              <!-- Search Form -->
              <div v-if="!propertyResult && !searchResult" class="form-card">
                <div class="form-card-header">
                  <div class="step-badge">
                    <span class="step-number">1</span>
                    <span class="step-text">Search Property</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <p class="form-intro">
                    Enter your plot number or LR number to check your land rates balance and make a payment.
                  </p>

                  <div class="search-form">
                    <div class="form-group">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        Plot / LR Number
                      </label>
                      <div class="input-with-icon">
                        <input
                            v-model="keyword"
                            type="text"
                            class="form-input"
                            placeholder="e.g., LR/12345 or PLOT-001"
                            @keyup.enter="searchProperty"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                          </svg>
                        </span>
                      </div>
                      <span class="form-hint">Enter the plot number exactly as it appears on your documents</span>
                    </div>

                    <!-- Error Message -->
                    <div v-if="message" class="alert alert-error">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      <span>{{ message }}</span>
                      <button class="alert-close" @click="message = null">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>

                    <button
                        @click="searchProperty"
                        class="btn btn-primary btn-block"
                        :disabled="loading"
                    >
                      <div v-if="loading" class="spinner white"></div>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <span>{{ loading ? 'Searching...' : 'Check Status' }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- ============================================================
                   Masked-result panel (between search and reveal).
                   Citizen confirms it's their property and requests an OTP
                   to the phone on file before any private data is shown.
                   ============================================================ -->
              <div v-if="searchResult && !propertyResult" class="form-card">
                <div class="form-card-header">
                  <button class="back-btn" @click="goBack">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                    Back
                  </button>
                  <div class="step-badge success">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    <span>Property Found</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <p style="margin:0 0 16px;color:#475569;line-height:1.5;font-size:14px;">
                    For your privacy, we need to confirm you are the owner of this property before showing the balance.
                  </p>

                  <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:16px 20px;margin-bottom:18px;">
                    <div style="display:flex;justify-content:space-between;font-size:14px;padding:6px 0;border-bottom:1px dashed #e2e8f0;">
                      <span style="color:#6b7280;">LR / Plot No.</span>
                      <strong>{{ searchResult.LRNumber }}</strong>
                    </div>
                    <div v-if="searchResult.UPN" style="display:flex;justify-content:space-between;font-size:14px;padding:6px 0;border-bottom:1px dashed #e2e8f0;">
                      <span style="color:#6b7280;">UPN</span>
                      <strong>{{ searchResult.UPN }}</strong>
                    </div>
                    <div v-if="searchResult.subCountyName" style="display:flex;justify-content:space-between;font-size:14px;padding:6px 0;border-bottom:1px dashed #e2e8f0;">
                      <span style="color:#6b7280;">Sub-County</span>
                      <strong>{{ searchResult.subCountyName }}</strong>
                    </div>
                    <div style="display:flex;justify-content:space-between;font-size:14px;padding:6px 0;border-bottom:1px dashed #e2e8f0;">
                      <span style="color:#6b7280;">Owner</span>
                      <strong>{{ searchResult.ownerNamesMasked || '—' }}</strong>
                    </div>
                    <div style="display:flex;justify-content:space-between;font-size:14px;padding:6px 0;">
                      <span style="color:#6b7280;">Phone on file</span>
                      <strong>{{ searchResult.ownerPhoneMasked || '—' }}</strong>
                    </div>
                  </div>

                  <div v-if="!searchResult.hasPhoneOnFile" class="alert alert-error" style="display:block;">
                    <strong>Phone number not on file.</strong>
                    <p style="margin:6px 0 0;font-size:13px;line-height:1.5;">
                      We don't have a phone number registered for this property, so we can't verify your identity online.
                      Please visit the County Revenue Board office (with your ID and ownership documents) so we can update your records.
                      Once updated, you'll be able to view balances and pay from this portal.
                    </p>
                  </div>

                  <button
                      v-else
                      @click="requestOtp"
                      class="btn btn-primary btn-block"
                      :disabled="otpRequesting">
                    <div v-if="otpRequesting" class="spinner white"></div>
                    <span v-else>Send code to {{ searchResult.ownerPhoneMasked }}</span>
                  </button>
                </div>
              </div>

              <!-- Property Details & Payment -->
              <div v-if="propertyResult" class="form-card">
                <div class="form-card-header">
                  <button class="back-btn" @click="goBack">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                  </button>
                  <div class="step-badge success">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Property Found</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <!-- Property Header -->
                  <div class="property-header">
                    <div class="property-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div class="property-info">
                      <h2 class="property-owner">{{ property.ownerNames }}</h2>
                      <span class="property-number">{{ property.LRNumber }}</span>
                    </div>
                  </div>

                  <!-- Property Details Grid -->
                  <div class="details-grid">
                    <div class="detail-item">
                      <span class="detail-label">Sub County</span>
                      <span class="detail-value">{{ property.subCountyName || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Ward</span>
                      <span class="detail-value">{{ property.wardName || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Zone</span>
                      <span class="detail-value">{{ property.zone || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Land Use</span>
                      <span class="detail-value">{{ property.useName || 'N/A' }}</span>
                    </div>
                  </div>

                  <!-- Balance Card -->
                  <div class="balance-card">
                    <div class="balance-row">
                      <div class="balance-item">
                        <span class="balance-label">Annual Charges</span>
                        <span class="balance-value">KES {{ formatNumber(property.annualCharges) }}</span>
                      </div>
                      <div class="balance-divider"></div>
                      <div class="balance-item highlight">
                        <span class="balance-label">Outstanding Balance</span>
                        <span class="balance-value large">KES {{ formatNumber(property.outstandingArrears) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Payment Form -->
                  <div class="payment-form">
                    <h3 class="section-title">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                      Make Payment
                    </h3>

                    <div class="form-grid">
                      <div class="form-group">
                        <label class="form-label">Amount to Pay (KES)</label>
                        <div class="input-with-currency">
                          <span class="currency-badge">KES</span>
                          <input
                              v-model="amountToPay"
                              type="number"
                              class="form-input"
                              placeholder="0.00"
                          />
                        </div>
                        <div class="quick-amounts">
                          <button
                              type="button"
                              class="quick-btn"
                              @click="amountToPay = property.annualCharges"
                          >
                            Annual ({{ formatNumber(property.annualCharges) }})
                          </button>
                          <button
                              type="button"
                              class="quick-btn"
                              @click="amountToPay = property.outstandingArrears"
                          >
                            Full Balance
                          </button>
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">M-Pesa Phone Number</label>
                        <div class="input-with-icon">
                          <input
                              v-model="phoneNumber"
                              type="tel"
                              class="form-input"
                              placeholder="e.g., 0712345678"
                          />
                          <span class="input-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                              <line x1="12" y1="18" x2="12.01" y2="18"></line>
                            </svg>
                          </span>
                        </div>
                        <span class="form-hint">You'll receive an STK push to this number</span>
                      </div>
                    </div>

                    <button
                        @click="generateBill"
                        class="btn btn-success btn-block btn-lg"
                        :disabled="!amountToPay || !phoneNumber || paymentLoading"
                    >
                      <div v-if="paymentLoading" class="spinner white"></div>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                      <span>{{ paymentLoading ? 'Processing...' : `Pay KES ${formatNumber(amountToPay || 0)}` }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- ============================================================
         OTP modal (CR-2026-05-06-02). Gates the reveal of full
         property data behind a code sent to the registered phone.
         ============================================================ -->
    <transition name="modal">
      <div v-if="otpModalOpen" class="modal-overlay" @click.self="closeOtp">
        <div class="modal-container" style="max-width:440px;background:#fff;border-radius:16px;padding:28px;box-shadow:0 20px 60px rgba(0,0,0,0.25);">
          <h3 style="margin:0 0 6px;color:#0045a1;font-size:20px;font-weight:800;">Enter your verification code</h3>
          <p style="margin:0 0 18px;color:#475569;font-size:14px;line-height:1.5;">
            We sent a code to <strong>{{ otpPhoneMasked }}</strong>. Enter it below to view your land rate balance and pay.
          </p>
          <label style="display:block;font-size:12px;color:#6b7280;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:8px 0 6px;">Code</label>
          <input type="text" inputmode="numeric"
                 v-model="otpCode"
                 :placeholder="'Enter the code'"
                 autofocus
                 style="width:100%;padding:14px;border:2px solid #e5e7eb;border-radius:10px;font-size:18px;letter-spacing:4px;text-align:center;font-family:ui-monospace,monospace;">
          <div v-if="otpError" style="background:#fee2e2;color:#991b1b;padding:10px 14px;border-radius:8px;font-size:13px;margin-top:14px;">{{ otpError }}</div>
          <div style="display:flex;gap:10px;margin-top:18px;">
            <button class="btn btn-secondary" @click="closeOtp" style="flex:1;">Cancel</button>
            <button class="btn btn-primary" @click="verifyOtp" :disabled="otpStep === 'verifying' || !otpCode" style="flex:1;">
              <span v-if="otpStep === 'verifying'">Verifying…</span>
              <span v-else>Verify</span>
            </button>
          </div>
          <div style="margin-top:14px;text-align:center;">
            <button @click="requestOtp" class="btn-link" :disabled="otpRequesting" style="background:none;border:0;color:#2563eb;font-size:13px;cursor:pointer;text-decoration:underline;">
              Didn't get the code? Resend
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Payment Modal -->
    <transition name="modal">
      <div v-if="payConfirmationPop" class="modal-overlay">
        <div class="modal-container payment-modal">
          <!-- Payment Header -->
          <div class="payment-modal-header" :class="paymentReceived ? 'success' : 'pending'">
            <div v-if="!paymentReceived" class="payment-loader">
              <div class="loader-ring"></div>
            </div>
            <div v-else class="payment-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <p class="payment-status-text">
              {{ paymentReceived ? 'Payment Successful!' : `Processing ${feesAndCharge.feeDescription} Payment` }}
            </p>
            <h2 class="payment-amount">KES {{ formatNumber(amountToPay) }}</h2>
          </div>

          <!-- Payment Body -->
          <div class="payment-modal-body">
            <!-- Instructions (while waiting) -->
            <div v-if="!paymentReceived" class="payment-instructions">
              <h4>Payment Instructions</h4>
              <ol>
                <li>
                  <span class="step-num">1</span>
                  Check for an <strong>M-Pesa prompt</strong> on your phone
                </li>
                <li>
                  <span class="step-num">2</span>
                  Enter your <strong>M-Pesa PIN</strong> and confirm
                </li>
                <li>
                  <span class="step-num">3</span>
                  Wait for <strong>confirmation SMS</strong> from M-Pesa
                </li>
                <li>
                  <span class="step-num">4</span>
                  This page will update automatically (up to <strong>45 seconds</strong>)
                </li>
              </ol>
              <div class="waiting-indicator">
                <div class="pulse-dot"></div>
                <span>Waiting for payment confirmation...</span>
              </div>
            </div>

            <!-- Success Content -->
            <div v-if="paymentReceived" class="payment-success">
              <div class="success-details">
                <div class="success-row">
                  <span>Transaction Code</span>
                  <strong>{{ transaction.transaction_code }}</strong>
                </div>
                <div class="success-row">
                  <span>Paid By</span>
                  <strong>{{ transaction.names }}</strong>
                </div>
                <div class="success-row">
                  <span>Phone Number</span>
                  <strong>{{ transaction.msisdn }}</strong>
                </div>
              </div>

              <div class="success-actions">
                <button @click="printReceipt" class="btn btn-primary">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                  Print Receipt
                </button>
                <button @click="closePaymentModal" class="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <FooterComponent />
  </div>
</template>

<script>
import { paysol, biller, execute, execute2, authUrl, landrate } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "LandRates",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      loading: false,
      paymentLoading: false,
      message: null,
      propertyResult: false,
      keyword: '',
      phoneNumber: null,
      amountToPay: null,
      payConfirmationPop: false,
      loadingPayment: false,
      paymentReceived: false,

      // CR-2026-05-06-02 — OTP privacy gate.
      // searchResult holds masked card data from searchProperty; OTP state
      // governs the modal between masked card and full reveal; accessToken
      // is the short-lived bearer that authorises getPropertyForDemandNotice.
      searchResult: null,
      otpModalOpen: false,
      otpStep: 'request',          // 'request' | 'enter' | 'verifying'
      otpRequestId: null,
      otpCode: '',
      otpPhoneMasked: '',
      otpError: null,
      otpRequesting: false,
      accessToken: null,

      property: {
        id: null,
        UPN: null,
        LRNumber: null,
        subCountyName: null,
        subCountyID: null,
        wardName: null,
        wardID: null,
        percelSize: null,
        lat: null,
        lng: null,
        percelAddress: null,
        ownerNames: null,
        ownerEmail: null,
        ownerPhoneNo: null,
        outstandingArrears: null,
        annualCharges: null,
        createdBy: null,
        createdByIDNo: null,
        dateCreated: null,
        zone: null,
        block: null,
        useName: null
      },

      selectedProperties: [],
      billItem: [],
      billDetails: {},
      billInfo: [],
      feesAndCharges: [],
      feesAndCharge: {
        feeId: null,
        feeDescription: 'Land Rates',
        unitFeeAmount: null,
        accountNo: null,
        incomeTypeId: null,
        feeType: null,
        accountDesc: null,
        costCenterNo: null,
        zone: null,
        typeDescription: null
      },
      feesAndChargeItems: [],
      feeID: null,
      unitFeeAmount: null,
      totalAmount: 0,

      billGenerated: {
        billNo: null,
        payBillNo: null,
        amount: null,
        success: null
      },

      transaction: {
        id: null,
        transaction_code: null,
        names: null,
        msisdn: null,
        amount: null
      },

      receiptDetails: {
        receiptNo: null
      },

      billingModule: {
        incomeTypes: []
      },

      county: {}
    };
  },

  mounted() {
    this.getCounty();
    this.getIncomeTypes();
  },

  methods: {
    formatNumber(num) {
      if (!num) return '0';
      return new Intl.NumberFormat('en-KE').format(num);
    },

    goBack() {
      this.propertyResult = false;
      this.searchResult = null;
      this.accessToken = null;
      this.otpRequestId = null;
      this.otpCode = '';
      this.otpPhoneMasked = '';
      this.otpError = null;
      this.otpModalOpen = false;
      this.otpStep = 'request';
      this.message = null;
      this.amountToPay = null;
      this.phoneNumber = null;
    },

    closeOtp() {
      this.otpModalOpen = false;
      this.otpError = null;
      this.otpCode = '';
    },

    // CR-2026-05-06-02 — replaces direct getProperty call. Returns
    // a masked card; full data is only revealed after OTP verification.
    searchProperty() {
      if (!this.keyword) {
        this.message = "Please enter a plot number";
        return;
      }
      this.loading = true;
      this.message = null;
      this.searchResult = null;
      this.accessToken = null;

      const data = new FormData();
      data.append("function", "searchProperty");
      data.append("keyword", this.keyword);

      execute(data, landrate)
          .then((res) => {
            this.loading = false;
            if (res.data && res.data.success) {
              this.searchResult = res.data.data;
            } else {
              this.message = (res.data && res.data.message) || "Property not found.";
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message || "Network error — please try again.";
          });
    },

    requestOtp() {
      if (!this.searchResult) return;
      this.otpRequesting = true;
      this.otpError = null;

      const data = new FormData();
      data.append("function", "requestPropertyOTP");
      data.append("propertyId", this.searchResult.id);

      execute(data, landrate)
          .then((res) => {
            this.otpRequesting = false;
            if (res.data && res.data.success) {
              this.otpRequestId = res.data.data.requestId;
              this.otpPhoneMasked = res.data.data.phoneMasked;
              this.otpModalOpen = true;
              this.otpStep = 'enter';
              this.otpCode = '';
            } else {
              this.message = (res.data && res.data.message) || "Couldn't send code. Try again.";
            }
          })
          .catch((e) => {
            this.otpRequesting = false;
            this.message = e.message || "Network error — please try again.";
          });
    },

    verifyOtp() {
      if (!this.otpCode || !this.otpRequestId) return;
      this.otpStep = 'verifying';
      this.otpError = null;

      const data = new FormData();
      data.append("function", "verifyPropertyOTP");
      data.append("requestId", this.otpRequestId);
      data.append("propertyId", this.searchResult.id);
      data.append("otp", this.otpCode);

      execute(data, landrate)
          .then((res) => {
            if (res.data && res.data.success) {
              this.accessToken = res.data.data.accessToken;
              this.otpModalOpen = false;
              // Now fetch the full property using the access token, then
              // hand off to the existing reveal/pay flow by setting
              // this.property + propertyResult = true.
              this._fetchFullProperty();
            } else {
              this.otpStep = 'enter';
              this.otpError = (res.data && res.data.message) || "Invalid code.";
            }
          })
          .catch((e) => {
            this.otpStep = 'enter';
            this.otpError = e.message || "Network error — please try again.";
          });
    },

    _fetchFullProperty() {
      const data = new FormData();
      data.append("function", "getPropertyForDemandNotice");
      data.append("propertyId", this.searchResult.id);
      data.append("accessToken", this.accessToken);

      execute(data, landrate)
          .then((res) => {
            if (res.data && res.data.success) {
              this.property = res.data.data.property;
              this.selectedProperties = [res.data.data.property];
              this.propertyResult = true;
              this.amountToPay = this.property.outstandingArrears;
            } else {
              this.message = (res.data && res.data.message) || "Couldn't load property.";
            }
          })
          .catch((e) => {
            this.message = e.message || "Network error — please try again.";
          });
    },

    closePaymentModal() {
      this.payConfirmationPop = false;
      this.paymentReceived = false;
      this.loadingPayment = false;
      this.goBack();
    },

    getCounty() {
      const data = new FormData();
      data.append("function", "getCounty");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.county = res.data.data.county;
            }
          })
          .catch((e) => {
            console.error(e);
          });
    },

    getProperty() {
      if (!this.keyword) {
        this.message = "Please enter a plot number";
        return;
      }

      this.loading = true;
      this.message = null;

      const data = new FormData();
      data.append("function", "getProperty");
      data.append("keyword", this.keyword);

      execute(data, landrate)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.property = res.data.data.property;
              this.selectedProperties = [res.data.data.property];
              this.propertyResult = true;
              // Pre-fill amount with outstanding arrears
              this.amountToPay = this.property.outstandingArrears;
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message;
          });
    },

    getIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "LANDRATE");
      data.append("keyword", "Land Rate");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.billingModule.incomeTypes = res.data.data.incomeTypes;
              if (this.billingModule.incomeTypes.length > 0) {
                this.getFeesAndChargesBilling(this.billingModule.incomeTypes[0].incomeTypeId);
              }
            }
          })
          .catch((e) => {
            console.error(e);
          });
    },

    getFeesAndChargesBilling(incomeTypeId) {
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.feesAndCharges = res.data.data.feesAndCharges;
              if (this.feesAndCharges.length > 0) {
                this.feesAndCharge = this.feesAndCharges[0];
                this.feeID = this.feesAndCharge.feeId;
                this.unitFeeAmount = this.feesAndCharge.unitFeeAmount;
              }
            }
          })
          .catch((e) => {
            console.error(e);
          });
    },

    generateBill() {
      if (!this.amountToPay || this.amountToPay <= 0) {
        this.message = "Please enter a valid amount";
        return;
      }
      if (!this.phoneNumber) {
        this.message = "Please enter your M-Pesa phone number";
        return;
      }

      this.paymentLoading = true;
      this.generateFeeAndChargesItem();

      const data = new FormData();
      data.append("function", "generateBill3");
      data.append("billItem", JSON.stringify(this.billItem));

      execute(data, biller)
          .then((res) => {
            this.paymentLoading = false;
            if (res.data.success) {
              this.billDetails = res.data.data.bill.billDetails;
              this.billInfo = res.data.data.bill.billInfo;
              this.billGenerated = res.data.data.billGenerated;
              this.customerPayBillOnline();
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.paymentLoading = false;
            this.message = e.message;
          });
    },

    generateFeeAndChargesItem() {
      this.feesAndChargeItems = [];
      this.billItem = [];

      for (let item of this.selectedProperties) {
        this.feesAndChargeItems.push({
          feeId: this.feeID,
          unitFeeAmount: this.amountToPay,
          customer: item.LRNumber
        });
      }

      this.addToBill();
    },

    addToBill() {
      for (const item of this.feesAndChargeItems) {
        this.billItem.push({
          typeDescription: this.feesAndCharge.typeDescription,
          feeDescription: this.feesAndCharge.feeDescription,
          unitFeeAmount: item.unitFeeAmount,
          accountNo: this.feesAndCharge.accountNo,
          costCenterNo: this.feesAndCharge.costCenterNo,
          incomeTypeId: this.feesAndCharge.incomeTypeId,
          revenueStreamItem: this.property.LRNumber,
          feeId: item.feeId,
          amount: item.unitFeeAmount,
          customer: item.customer,
          zone: this.property.zone,
          subCountyID: this.property.subCountyID,
          subCountyName: this.property.subCountyName,
          wardID: this.property.wardID,
          wardName: this.property.wardName,
          idNo: sessionStorage.getItem("sessionIdNo"),
          phoneNumber: sessionStorage.getItem("sessionPhoneNumber"),
          names: sessionStorage.getItem("sessionNames"),
          customerPhoneNumber: this.property.ownerPhoneNo,
          description: this.property.LRNumber
        });
      }
    },

    customerPayBillOnline() {
      this.payConfirmationPop = true;
      this.loadingPayment = true;
      this.paymentReceived = false;

      const data = new FormData();
      data.append("function", "customerPayBillOnline");
      data.append("payBillNumber", this.billGenerated.payBillNo);
      data.append("amount", this.billGenerated.amount);
      data.append("accountReference", this.billGenerated.billNo);
      data.append("transactionDesc", this.billGenerated.billNo);
      data.append("phoneNumber", this.phoneNumber);
      data.append("token", "im05WXYH2rwRruPjCICieOs8m4E8IoltnDEhyPUv6bnB9cU60gD48SnJPC6oh7EpsPaAUGC8wqIdtVVjGlWLxqFssshxMHxHjEQJ");

      execute2(data, paysol)
          .then((res) => {
            if (res.data.success) {
              const intervalId = setInterval(() => {
                this.checkPayment()
                    .then((result) => {
                      if (result) {
                        clearInterval(intervalId);
                      }
                    })
                    .catch((error) => {
                      console.error(error);
                    });
              }, 2000);
            } else {
              this.loadingPayment = false;
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loadingPayment = false;
            this.message = e.message;
          });
    },

    checkPayment() {
      const data = new FormData();
      data.append("function", "checkPayment");
      data.append("accountRef", this.billGenerated.billNo);

      return new Promise((resolve, reject) => {
        execute(data, biller)
            .then((res) => {
              if (res.data.success) {
                this.loadingPayment = false;
                this.transaction = res.data.data.transaction;
                this.receiptDetails = res.data.data.receipt.receiptDetails;
                this.paymentReceived = true;
                resolve(true);
              } else {
                resolve(false);
              }
            })
            .catch((error) => {
              reject(error);
            });
      });
    },

    printReceipt() {
      if (!this.receiptDetails.receiptNo) {
        alert("Receipt not available yet. Please try again.");
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: this.receiptDetails.receiptNo
      });
      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Content Section */
.content-section {
  padding: 48px 0 80px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 32px;
  align-items: start;
}

/* Info Panel */
.info-panel {
  position: sticky;
  top: 96px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
}

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.info-description {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
}

.info-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
}

.feature-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #ede9fe; color: #7c3aed; }

.feature-item strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 2px;
}

.feature-item p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.info-help {
  padding: 20px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
}

.info-help h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
}

.info-help ul {
  margin: 0;
  padding-left: 24px;
}

.info-help li {
  font-size: 13px;
  color: #a16207;
  margin-bottom: 6px;
}

/* Form Panel */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-card-header {
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.step-badge {
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-badge .step-number {
  width: 28px;
  height: 28px;
  background: #0f4c81;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
}

.step-badge .step-text {
  font-weight: 600;
  color: #1e293b;
}

.step-badge.success {
  background: #d1fae5;
  color: #059669;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.form-card-body {
  padding: 32px;
}

.form-intro {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Form Elements */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.input-with-icon {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  padding-right: 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.input-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-hint {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
}

.input-with-currency {
  position: relative;
}

.input-with-currency .form-input {
  padding-left: 60px;
}

.currency-badge {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.quick-amounts {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.quick-btn {
  flex: 1;
  padding: 8px 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: inherit;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block { width: 100%; }
.btn-lg { padding: 16px 32px; font-size: 16px; }

.btn-primary {
  background: #0f4c81;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0a3459;
}

.btn-success {
  background: #059669;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #047857;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.white {
  border-color: rgba(255,255,255,0.3);
  border-top-color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Property Header */
.property-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-radius: 14px;
  margin-bottom: 24px;
}

.property-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.property-owner {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.property-number {
  font-size: 14px;
  color: #059669;
  font-weight: 600;
  font-family: monospace;
  background: rgba(5, 150, 105, 0.15);
  padding: 4px 10px;
  border-radius: 6px;
}

/* Details Grid */
.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-label {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

/* Balance Card */
.balance-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #fbbf24;
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 32px;
}

.balance-row {
  display: flex;
  align-items: center;
}

.balance-item {
  flex: 1;
  text-align: center;
}

.balance-item.highlight {
  background: rgba(255,255,255,0.5);
  padding: 16px;
  border-radius: 10px;
}

.balance-label {
  display: block;
  font-size: 12px;
  color: #92400e;
  margin-bottom: 6px;
  text-transform: uppercase;
  font-weight: 500;
}

.balance-value {
  font-size: 18px;
  font-weight: 700;
  color: #78350f;
}

.balance-value.large {
  font-size: 24px;
}

.balance-divider {
  width: 1px;
  height: 60px;
  background: rgba(146, 64, 14, 0.2);
  margin: 0 24px;
}

/* Payment Form */
.payment-form {
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 24px;
}

/* Payment Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 440px;
  width: 100%;
  overflow: hidden;
}

.payment-modal-header {
  padding: 40px 32px;
  text-align: center;
  color: white;
}

.payment-modal-header.pending {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
}

.payment-modal-header.success {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.payment-loader {
  margin-bottom: 20px;
}

.loader-ring {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255,255,255,0.2);
  border-top-color: white;
  border-radius: 50%;
  margin: 0 auto;
  animation: spin 1s linear infinite;
}

.payment-success-icon {
  width: 80px;
  height: 80px;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.payment-status-text {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.payment-amount {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
}

.payment-modal-body {
  padding: 32px;
}

.payment-instructions h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
}

.payment-instructions ol {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
}

.payment-instructions li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #475569;
}

.payment-instructions li:last-child {
  border-bottom: none;
}

.step-num {
  width: 24px;
  height: 24px;
  background: #eff6ff;
  color: #2563eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.waiting-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  background: #fffbeb;
  border-radius: 10px;
  font-size: 14px;
  color: #92400e;
}

.pulse-dot {
  width: 10px;
  height: 10px;
  background: #f59e0b;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}

.payment-success {
  text-align: center;
}

.success-details {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.success-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
}

.success-row:last-child {
  border-bottom: none;
}

.success-row span {
  color: #64748b;
}

.success-row strong {
  color: #1e293b;
}

.success-actions {
  display: flex;
  gap: 12px;
}

.success-actions .btn {
  flex: 1;
}

/* Transitions */
.modal-enter-active, .modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(20px);
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-panel {
    position: static;
  }
}

@media (max-width: 768px) {
  /* Form panel first on mobile (same pattern as DailyParking/HouseRents/PayBill). */
  .content-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .info-panel { order: 2; }
  .form-panel { order: 1; }

  /* Hero shrunk. */
  .container { padding: 0 12px; }
  .page-hero { padding: 20px 0 40px; }
  .page-title { font-size: 20px !important; margin-bottom: 4px; }
  .hero-subtitle,
  .breadcrumb,
  .breadcrumb-current { font-size: 12px; }
  .content-section { padding: 24px 0 32px; }

  /* Info card (now below the form) — compact. */
  .info-card { padding: 16px 14px; border-radius: 12px; }
  .info-card-header { gap: 10px; }
  .info-icon { width: 40px; height: 40px; }
  .info-icon svg { width: 20px; height: 20px; }
  .info-card h3 { font-size: 16px; }
  .info-card .card-subtitle,
  .info-card p { font-size: 12.5px; line-height: 1.4; }
  .info-features { gap: 8px; margin-top: 12px; }
  .info-feature { padding: 10px; gap: 8px; }
  .info-feature-icon { width: 28px; height: 28px; }
  .info-feature-icon svg { width: 14px; height: 14px; }
  .info-feature strong { font-size: 12.5px; }
  .info-feature p,
  .info-feature .text-muted { font-size: 11px; margin-top: 2px; }
  .tips-card { padding: 14px; border-radius: 10px; margin-top: 12px; }
  .tips-card h4,
  .tips-card .tips-title { font-size: 13px; margin-bottom: 6px; }
  .tips-card ul { padding-left: 16px; margin: 0; }
  .tips-card li { font-size: 12px; line-height: 1.5; }

  /* Form-card body. */
  .form-card { border-radius: 12px; }
  .form-card-header { padding: 12px 14px; }
  .form-card-header .step-text { font-size: 14px; }
  .form-card-body {
    padding: 16px 14px;
  }
  .form-intro { font-size: 12.5px; margin-bottom: 12px; }
  .form-group { margin-bottom: 12px; }
  .form-label { font-size: 12.5px; margin-bottom: 4px; }
  .form-input,
  .form-select { font-size: 14px; padding: 10px 12px; min-height: 42px; }

  .form-grid,
  .details-grid { grid-template-columns: 1fr; gap: 10px; }

  .balance-row { flex-direction: column; gap: 12px; }
  .balance-divider { width: 100%; height: 1px; margin: 0; }
  .property-header { flex-direction: column; text-align: center; gap: 8px; padding: 14px; }
  .success-actions { flex-direction: column; gap: 10px; }
}

@media (max-width: 480px) {
  .container { padding: 0 10px; }
  .page-title { font-size: 18px !important; }
  .form-card-body { padding: 14px 12px; }
  .form-input, .form-select { font-size: 13.5px; min-height: 40px; }
}
</style>